import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useDashboardData } from "../hooks/useDashboardData";
import api from "../lib/api";
import { Sidebar } from "../components/Sidebar";
import { ModuleNavBar } from "../components/ModuleNavBar";
import { CaptureCard } from "../components/CaptureCard";
import { TrashCaptureCard } from "../components/TrashCaptureCard";
import { NotesWorkspace } from "../components/NotesWorkspace";
import { NewTagDialog } from "../components/NewTagDialog";
import { NewFolderDialog } from "../components/NewFolderDialog";
import { AddTagDialog } from "../components/AddTagDialog";
import { BulkAddTagDialog } from "../components/BulkAddTagDialog";
import { SendEmailModal } from "../components/SendEmailModal";
import { ShareLinkDialog } from "../components/ShareLinkDialog";
import { MergeLayoutDialog } from "../components/MergeLayoutDialog";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { PaperPlaneTilt, ImageSquare, UploadSimple, NotePencil, Tag as TagIcon, Trash, CaretDown, FilePdf, LinkSimple, ArrowCounterClockwise, Recycle, Stack, SortAscending, SortDescending, ClockCounterClockwise, CalendarBlank, SquaresFour, Rows } from "@phosphor-icons/react";
import { downloadCapturesPdf } from "../lib/pdf";
import { ALL_CAPTURES_FILTER, TODAY_FILTER } from "../lib/captureFilters";
import { CaptureEditDialog } from "../components/CapturePreviewDialog";

// One global on/off switch (mirrors the notes-editor toolbar toggle in
// CaptureEditPanel.jsx) for the whole page-header row above the notes grid
// (breadcrumb label + sort/new-note/upload buttons) - hiding it gives the
// split-view note editors below more vertical room, per user request.
const HEADER_COLLAPSED_KEY = "dashboardHeaderCollapsed";
// Must stay in sync with CaptureEditPanel.jsx's own copies of these two -
// they're duplicated (no shared constants module exists yet) so that Focus
// Mode below can flip the SAME localStorage key the individual per-note
// toolbar toggle reads/writes, and broadcast the change to any already-open
// note panes via the same custom event so they update without a remount.
const NOTE_TOOLBAR_COLLAPSED_KEY = "notesToolbarCollapsed";
const NOTE_TOOLBAR_COLLAPSED_EVENT = "notes-toolbar-collapsed-changed";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const d = useDashboardData();
  const isTrash = d.viewMode === "trash";
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const preFocusStateRef = useRef(null);

  // Header/toolbar collapse is now only ever driven by Focus Mode (the old
  // standalone per-toggle buttons were removed) - focusMode itself resets to
  // false on every mount, so these two localStorage flags must never be
  // allowed to "outlive" a session. Without this, a browser that had Focus
  // Mode on when the tab was closed/reloaded would restart with the header
  // hidden and no way to bring it back (exiting Focus Mode just restores the
  // still-collapsed pre-focus value). Force-clear both on every fresh mount.
  useEffect(() => {
    localStorage.setItem(HEADER_COLLAPSED_KEY, "0");
    localStorage.setItem(NOTE_TOOLBAR_COLLAPSED_KEY, "0");
    window.dispatchEvent(new Event(NOTE_TOOLBAR_COLLAPSED_EVENT));
  }, []);
  // Notes page has two mutually-exclusive layouts (not persisted - always
  // starts on Grid per user request): Grid shows the card grid and opens a
  // note in the existing CaptureEditDialog modal; Edit shows only the
  // tabbed split-view workspace for the tabs already open (opened via the
  // Sidebar's per-note links, which switch into this layout automatically).
  const [viewLayout, setViewLayout] = useState("grid");

  // Deep-link support for the global search dropdown (GlobalSearchBox) -
  // it navigates here with ?openNote=<id> and this fetches + opens that
  // exact note regardless of whatever grid/tag filter is currently active,
  // then strips the query param so a refresh doesn't reopen it.
  useEffect(() => {
    const openNoteId = new URLSearchParams(location.search).get("openNote");
    if (!openNoteId) return;
    api.get(`/captures/${openNoteId}`).then(({ data }) => {
      setViewLayout("grid");
      d.setEditCapture(data);
    }).catch(() => {
      toast.error(t("common.genericError"));
    }).finally(() => {
      navigate("/dashboard", { replace: true });
    });
  }, [location.search]);

  // One-click "focus mode": hides the sidebar, the page header row, and
  // every open note's toolbar at once for distraction-free writing.
  // Remembers whatever state each of those three was already in so exiting
  // restores it exactly, instead of forcing everything back open.
  const toggleFocusMode = () => {
    if (!focusMode) {
      preFocusStateRef.current = {
        sidebarCollapsed,
        headerCollapsed,
        toolbarCollapsed: localStorage.getItem(NOTE_TOOLBAR_COLLAPSED_KEY) === "1",
      };
      setSidebarCollapsed(true);
      setHeaderCollapsed(true);
      localStorage.setItem(HEADER_COLLAPSED_KEY, "1");
      localStorage.setItem(NOTE_TOOLBAR_COLLAPSED_KEY, "1");
      window.dispatchEvent(new Event(NOTE_TOOLBAR_COLLAPSED_EVENT));
      setFocusMode(true);
    } else {
      const prev = preFocusStateRef.current || { sidebarCollapsed: false, headerCollapsed: false, toolbarCollapsed: false };
      setSidebarCollapsed(prev.sidebarCollapsed);
      setHeaderCollapsed(prev.headerCollapsed);
      localStorage.setItem(HEADER_COLLAPSED_KEY, prev.headerCollapsed ? "1" : "0");
      localStorage.setItem(NOTE_TOOLBAR_COLLAPSED_KEY, prev.toolbarCollapsed ? "1" : "0");
      window.dispatchEvent(new Event(NOTE_TOOLBAR_COLLAPSED_EVENT));
      setFocusMode(false);
    }
  };

  const handleMergeClick = () => {
    if (d.selectedCaptures.length < 2) {
      toast.error(t("dashboard.mergeNeedsMinError"));
      return;
    }
    if (d.selectedCaptures.some((c) => c.type !== "image")) {
      toast.error(t("dashboard.mergeNeedsImageOnlyError"));
      return;
    }
    if (d.selectedCaptures.length === 2) {
      setMergeDialogOpen(true);
    } else {
      d.handleMergeCaptures(null);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="dashboard-page">
      <ModuleNavBar active="notes" focusMode={focusMode} onToggleFocusMode={toggleFocusMode} />
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
      <Sidebar
        tags={d.tags}
        folders={d.folders}
        selectedTagId={d.selectedTagId}
        onSelectTag={d.selectTag}
        onNewTag={() => d.setNewTagOpen(true)}
        onDeleteTag={d.handleDeleteTag}
        onRenameTag={d.handleRenameTag}
        onGenerateTagLink={d.handleGenerateTagLink}
        onOpenTrash={d.openTrash}
        trashActive={isTrash}
        onNewFolder={() => d.setNewFolderOpen(true)}
        onDeleteFolder={d.handleDeleteFolder}
        onRenameFolder={d.handleRenameFolder}
        onMoveTagToFolder={d.handleMoveTagToFolder}
        onUpdateFolderColor={d.handleUpdateFolderColor}
        onBulkMoveTagsToFolder={d.handleBulkMoveTagsToFolder}
        captures={d.captures}
        onOpenNote={(c) => {
          d.openNoteTab(c);
          setViewLayout("edit");
        }}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
      />

      <main
        className="flex-1 overflow-y-auto p-6 md:p-10 relative"
        onDragEnter={isTrash ? undefined : d.handleDragEnter}
        onDragLeave={isTrash ? undefined : d.handleDragLeave}
        onDragOver={isTrash ? undefined : d.handleDragOver}
        onDrop={isTrash ? undefined : d.handleDrop}
      >
        {!isTrash && d.isDragging && (
          <div
            data-testid="dashboard-drop-overlay"
            className="absolute inset-0 z-50 bg-primary/10 border-4 border-dashed border-primary flex items-center justify-center pointer-events-none"
          >
            <p className="font-mono-tag text-lg text-primary bg-background px-6 py-3 border border-primary">{t("dashboard.dropHint")}</p>
          </div>
        )}
        {!headerCollapsed && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div>
            <p className="font-mono-tag text-xs text-primary mb-1">
              {isTrash
                ? "NOTE"
                : d.selectedTagId === ALL_CAPTURES_FILTER
                ? t("dashboard.allCapturesTitle")
                : d.selectedTagId === TODAY_FILTER
                ? t("sidebar.today")
                : d.selectedTagId
                ? d.tags.find((tag) => tag.id === d.selectedTagId)?.name
                : t("dashboard.untaggedTitle")}
            </p>
            {isTrash && (
              <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-3" data-testid="dashboard-view-title">
                <Recycle size={26} /> {t("dashboard.trashTitle")}
              </h1>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isTrash && (
              <>
                <div className="flex border border-border" data-testid="notes-view-layout-toggle">
                  <button
                    type="button"
                    data-testid="notes-view-layout-grid-button"
                    onClick={() => setViewLayout("grid")}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors ${
                      viewLayout === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <SquaresFour size={15} weight="bold" /> {t("dashboard.gridView")}
                  </button>
                  <button
                    type="button"
                    data-testid="notes-view-layout-edit-button"
                    onClick={() => setViewLayout("edit")}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold transition-colors border-l border-border ${
                      viewLayout === "edit" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Rows size={15} weight="bold" /> {t("dashboard.editView")}
                  </button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="sort-field-menu-button"
                      variant="outline"
                      className="rounded-none gap-2 hover:-translate-y-[2px] transition-transform font-semibold"
                    >
                      <CalendarBlank size={16} weight="bold" />
                      {d.sortField === "created_at" ? t("dashboard.sortByCreated") : t("dashboard.sortByModified")}
                      <CaretDown size={12} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none">
                    <DropdownMenuItem data-testid="sort-field-modified" onClick={() => d.setSortField("updated_at")} className="gap-2">
                      <ClockCounterClockwise size={14} /> {t("dashboard.sortByModified")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="sort-field-created" onClick={() => d.setSortField("created_at")} className="gap-2">
                      <CalendarBlank size={14} /> {t("dashboard.sortByCreated")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  data-testid="sort-order-toggle-button"
                  variant="outline"
                  onClick={d.toggleSortOrder}
                  title={d.sortOrder === "desc" ? t("dashboard.sortNewestFirst") : t("dashboard.sortOldestFirst")}
                  className="rounded-none gap-2 hover:-translate-y-[2px] transition-transform font-semibold"
                >
                  {d.sortOrder === "desc" ? <SortDescending size={18} weight="bold" /> : <SortAscending size={18} weight="bold" />}
                  {d.sortOrder === "desc" ? t("dashboard.sortNewestFirst") : t("dashboard.sortOldestFirst")}
                </Button>
              </>
            )}
            {isTrash ? (
              d.trashSelectedIds.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="trash-bulk-action-menu-button"
                      className="rounded-none gap-2 bg-primary hover:bg-primary/90 hover:-translate-y-[2px] transition-transform font-semibold"
                    >
                      {t("dashboard.action")}（{d.trashSelectedIds.length}） <CaretDown size={14} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none">
                    <DropdownMenuItem data-testid="trash-bulk-action-restore" onClick={d.handleBulkRestore} className="gap-2">
                      <ArrowCounterClockwise size={14} /> {t("dashboard.restore")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="trash-bulk-action-permanent-delete"
                      onClick={() => {
                        if (window.confirm(t("dashboard.confirmPermanentDeleteMany", { count: d.trashSelectedIds.length }))) {
                          d.handleBulkPermanentDelete();
                        }
                      }}
                      className="gap-2 text-destructive focus:text-destructive"
                    >
                      <Trash size={14} /> {t("dashboard.permanentDelete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            ) : (
              <>
                {d.selectedIds.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="bulk-action-menu-button"
                        className="rounded-none gap-2 bg-primary hover:bg-primary/90 hover:-translate-y-[2px] transition-transform font-semibold"
                      >
                        {t("dashboard.action")}（{d.selectedIds.length}） <CaretDown size={14} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-none">
                      <DropdownMenuItem data-testid="bulk-action-send" onClick={() => d.setSendEmailOpen(true)} className="gap-2">
                        <PaperPlaneTilt size={14} /> {t("dashboard.sendEmail")}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="bulk-action-tag" onClick={() => d.setBulkTagOpen(true)} className="gap-2">
                        <TagIcon size={14} /> {t("dashboard.addTag")}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="bulk-action-pdf" onClick={() => downloadCapturesPdf(d.selectedIds)} className="gap-2">
                        <FilePdf size={14} /> {t("dashboard.downloadPdf")}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="bulk-action-link" onClick={() => d.handleGenerateLink(d.selectedIds)} className="gap-2">
                        <LinkSimple size={14} /> {t("dashboard.generateLink")}
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="bulk-action-merge" onClick={handleMergeClick} className="gap-2">
                        <Stack size={14} /> {t("dashboard.merge")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="bulk-action-delete" onClick={d.handleBulkDelete} className="gap-2 text-destructive focus:text-destructive">
                        <Trash size={14} /> {t("dashboard.delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple data-testid="upload-screenshot-input" className="hidden" onChange={(e) => { d.handleUploadFiles(e.target.files); e.target.value = ""; }} />
                <Button
                  data-testid="new-text-note-button"
                  variant="outline"
                  onClick={d.handleCreateTextNote}
                  className="rounded-none gap-2 hover:-translate-y-[2px] transition-transform font-semibold"
                >
                  <NotePencil size={18} weight="bold" /> {t("dashboard.newTextNote")}
                </Button>
                <Button
                  data-testid="upload-screenshot-button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-none gap-2 hover:-translate-y-[2px] transition-transform font-semibold"
                >
                  <UploadSimple size={18} weight="bold" /> {t("dashboard.uploadScreenshot")}
                </Button>
              </>
            )}
          </div>
        </div>
        )}

        {!isTrash && viewLayout === "edit" && (
          <NotesWorkspace
            tabs={d.openTabs}
            focusedTabId={d.focusedTabId}
            onFocusTab={d.focusNoteTab}
            onCloseTab={d.closeNoteTab}
            onDelete={d.handleDeleteCapture}
            onSaved={d.handleCaptureSaved}
            pinnedTabId={d.pinnedTabId}
            onTogglePin={d.togglePinTab}
            onSwapTabs={d.swapTabs}
            onGenerateLink={d.handleGenerateLink}
            onAddTag={d.setTagCapture}
            tags={d.tags}
            onToggleFavorite={d.handleToggleFavorite}
          />
        )}

        {isTrash ? (
          d.trashCaptures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-border" data-testid="empty-trash-state">
              <Recycle size={48} className="text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t("dashboard.emptyTrash")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="trash-grid">
              {d.trashCaptures.map((c) => (
                <TrashCaptureCard
                  key={c.id}
                  capture={c}
                  selected={d.trashSelectedIds.includes(c.id)}
                  onToggleSelect={d.toggleTrashSelect}
                  onRestore={d.handleRestore}
                  onPermanentDelete={d.handlePermanentDelete}
                />
              ))}
            </div>
          )
        ) : viewLayout !== "grid" ? null : d.captures.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center border border-dashed border-border" data-testid="empty-captures-state">
            <ImageSquare size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">{t("dashboard.emptyCaptures")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="captures-grid">
            {d.captures.map((c) => (
              <CaptureCard
                key={c.id}
                capture={c}
                tags={d.tags}
                selected={d.selectedIds.includes(c.id)}
                onToggleSelect={d.toggleSelect}
                onOpen={d.setEditCapture}
                onDelete={d.handleDeleteCapture}
                onSendSingle={(cap) => {
                  d.setSingleSendCapture(cap);
                  d.setSendEmailOpen(true);
                }}
                onAddTag={d.setTagCapture}
                onGenerateLink={d.handleGenerateLink}
                onToggleFavorite={d.handleToggleFavorite}
              />
            ))}
          </div>
        )}
      </main>

      <CaptureEditDialog
        capture={viewLayout === "grid" ? d.editCapture : null}
        open={viewLayout === "grid" && !!d.editCapture}
        onOpenChange={(o) => !o && d.setEditCapture(null)}
        onDelete={d.handleDeleteCapture}
        onSaved={d.handleCaptureSaved}
      />

      <AddTagDialog
        capture={d.tagCapture}
        tags={d.tags}
        open={!!d.tagCapture}
        onOpenChange={(o) => !o && d.setTagCapture(null)}
        onTagged={d.handleCaptureTagged}
        onCreateTag={(t) => d.setTags((prev) => [t, ...prev])}
      />
      <BulkAddTagDialog
        captureIds={d.selectedIds}
        tags={d.tags}
        open={d.bulkTagOpen}
        onOpenChange={d.setBulkTagOpen}
        onTagged={d.handleBulkTagged}
        onCreateTag={(t) => d.setTags((prev) => [t, ...prev])}
      />
      <NewTagDialog open={d.newTagOpen} onOpenChange={d.setNewTagOpen} onCreated={(t) => d.setTags((prev) => [t, ...prev])} />
      <NewFolderDialog open={d.newFolderOpen} onOpenChange={d.setNewFolderOpen} onCreated={(f) => d.setFolders((prev) => [f, ...prev])} />
      <ShareLinkDialog open={d.shareLinkOpen} onOpenChange={d.setShareLinkOpen} url={d.shareLinkUrl} />
      <MergeLayoutDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen} onChoose={d.handleMergeCaptures} />
      <SendEmailModal
        open={d.sendEmailOpen}
        onOpenChange={(o) => {
          d.setSendEmailOpen(o);
          if (!o) d.setSingleSendCapture(null);
        }}
        selectedCaptures={d.emailTargets}
        userEmail={user?.email}
        onSent={() => {
          d.setSelectedIds([]);
          d.setSingleSendCapture(null);
        }}
      />
      </div>
    </div>
  );
}
